import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import "@/i18n";

import Layout from "@/components/Layout";
import ScrollToTop from "@/components/ScrollToTop";
import Home from "@/pages/Home";
import PagePlaceholder from "@/pages/PagePlaceholder";
import NotFound from "@/pages/NotFound";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import Terms from "@/pages/Terms";
import CookiePolicy from "@/pages/CookiePolicy";
import ProductDetailPage from "@/pages/ProductDetailPage";
import ProductionConsumablesPage from "@/pages/ProductionConsumablesPage";
import SolutionLandingPage from "@/pages/SolutionLandingPage";
import IndustryPharmaceutical from "@/pages/IndustryPharmaceutical";
import IndustryFood from "@/pages/IndustryFood";
import TechnicalSupport from "@/pages/TechnicalSupport";
import GlobalDistribution from "@/pages/GlobalDistribution";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Resources from "@/pages/Resources";
import RequestSample from "@/pages/RequestSample";
import Distributors from "@/pages/Distributors";
import BlogIndex from "@/pages/BlogIndex";
import BlogArticle from "@/pages/BlogArticle";
import FindTheRightInk from "@/pages/FindTheRightInk";

// Admin (Phase 5)
import { AdminAuthProvider } from "@/contexts/AdminAuthContext";
import RequireAdmin from "@/components/admin/RequireAdmin";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminLeadDetail from "@/pages/admin/AdminLeadDetail";
import AdminInsights from "@/pages/admin/AdminInsights";

const P = ({ title, description, breadcrumbs }) => (
  <PagePlaceholder title={title} description={description} breadcrumbs={breadcrumbs} />
);

const S = (slug) => <SolutionLandingPage slug={slug} />;

// Wrap only public routes in the public Layout so admin pages stay bare.
const Public = ({ children }) => <Layout>{children}</Layout>;

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <ScrollToTop />
        <AdminAuthProvider>
          <Routes>
            {/* Admin — bare layout, provider handles auth */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<RequireAdmin><AdminDashboard /></RequireAdmin>} />
            <Route path="/admin/insights" element={<RequireAdmin><AdminInsights /></RequireAdmin>} />
            <Route path="/admin/leads/:id" element={<RequireAdmin><AdminLeadDetail /></RequireAdmin>} />

            {/* Public — wrapped in Layout */}
            <Route path="/" element={<Public><Home /></Public>} />

            {/* Solutions — landing pages */}
            <Route path="/solutions/blister-printing" element={<Public>{S("blister-printing")}</Public>} />
            <Route path="/solutions/specialty-inks" element={<Public>{S("specialty-inks")}</Public>} />
            <Route path="/solutions/printing-aids" element={<Public>{S("printing-aids")}</Public>} />
            <Route path="/solutions/coding-marking" element={<Public>{S("coding-marking")}</Public>} />
            <Route
              path="/solutions/cleaning-maintenance"
              element={<Navigate to="/solutions/printing-aids" replace />}
            />
            <Route
              path="/solutions/production-consumables"
              element={<Navigate to="/products/production-consumables" replace />}
            />

            {/* Products */}
            <Route
              path="/products/production-consumables"
              element={<Public><ProductionConsumablesPage /></Public>}
            />
            <Route path="/products/:slug" element={<Public><ProductDetailPage /></Public>} />

            {/* Industries */}
            <Route path="/industries/pharmaceutical" element={<Public><IndustryPharmaceutical /></Public>} />
            <Route path="/industries/food" element={<Public><IndustryFood /></Public>} />

            {/* Support & distribution */}
            <Route path="/technical-support" element={<Public><TechnicalSupport /></Public>} />
            <Route path="/global-distribution" element={<Public><GlobalDistribution /></Public>} />
            <Route path="/distributors" element={<Public><Distributors /></Public>} />

            {/* Content */}
            <Route path="/resources" element={<Public><Resources /></Public>} />
            <Route path="/blog" element={<Public><BlogIndex /></Public>} />
            <Route path="/blog/:slug" element={<Public><BlogArticle /></Public>} />
            <Route path="/about" element={<Public><About /></Public>} />
            <Route path="/contact" element={<Public><Contact /></Public>} />
            <Route path="/request-sample" element={<Public><RequestSample /></Public>} />
            <Route path="/find-the-right-ink" element={<Public><FindTheRightInk /></Public>} />

            {/* Legal */}
            <Route path="/privacy-policy" element={<Public><PrivacyPolicy /></Public>} />
            <Route path="/terms" element={<Public><Terms /></Public>} />
            <Route path="/cookie-policy" element={<Public><CookiePolicy /></Public>} />

            {/* 404 */}
            <Route path="*" element={<Public><NotFound /></Public>} />
          </Routes>
        </AdminAuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
